import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { publicidadConfigService } from '../../../services/api';
import styles from './PublicidadTab.module.css';

const CANALES = ['impreso','digital','revista','omnicanal'];

// ─── Sección genérica CRUD con tabla + formulario inline ────────

function CrudSection({ title, items, columns, renderForm, onAdd, onEdit, onDelete, adding, editing }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {title}
        <button className={styles.btnPrimary} onClick={onAdd} style={{ marginLeft: 'auto', fontSize: 12, padding: '5px 12px' }}>
          <Plus size={13} /> Nuevo
        </button>
      </div>

      {(adding || editing) && renderForm()}

      {items.length === 0 && !adding ? (
        <div className={styles.empty}>No hay registros. Haz clic en Nuevo para crear.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th>Acciones</th></tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                {columns.map(c => (
                  <td key={c.key}>
                    {c.render ? c.render(item) : String(item[c.key] ?? '')}
                  </td>
                ))}
                <td>
                  <div className={styles.actions}>
                    <button className={styles.btnIcon} onClick={() => onEdit(item)}><Pencil size={12} /></button>
                    <button className={`${styles.btnIcon} ${styles.danger}`} onClick={() => onDelete(item.id)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Sección colapsable ──────────────────────────────────────────

function Collapsible({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 24 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display:'flex', alignItems:'center', gap:8, width:'100%', background:'none', border:'none', cursor:'pointer', padding:'10px 0', borderBottom:'2px solid #eee' }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ fontWeight:600, fontSize:14, color:'#333' }}>{title}</span>
      </button>
      {open && <div style={{ paddingTop: 16 }}>{children}</div>}
    </div>
  );
}

// ─── Formulario genérico ─────────────────────────────────────────

function FormInline({ fields, values, onChange, onSave, onCancel, saving }) {
  return (
    <div className={styles.form}>
      {fields.map(f => (
        <div key={f.key} className={styles.formGroup} style={f.full ? { gridColumn:'1/-1' } : {}}>
          <label>{f.label}{f.required && ' *'}</label>
          {f.type === 'select' ? (
            <select value={values[f.key] ?? ''} onChange={e => onChange(f.key, e.target.value)}>
              <option value=''>-- Selecciona --</option>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : f.type === 'textarea' ? (
            <textarea rows={3} value={values[f.key] ?? ''} onChange={e => onChange(f.key, e.target.value)} />
          ) : f.type === 'checkbox' ? (
            <label style={{ flexDirection:'row', gap:8, alignItems:'center', cursor:'pointer' }}>
              <input type='checkbox' checked={!!values[f.key]} onChange={e => onChange(f.key, e.target.checked)} />
              {f.checkLabel}
            </label>
          ) : (
            <input
              type={f.type || 'text'}
              value={values[f.key] ?? ''}
              onChange={e => onChange(f.key, f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
              placeholder={f.placeholder}
            />
          )}
        </div>
      ))}
      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onCancel}>Cancelar</button>
        <button className={styles.btnPrimary} onClick={onSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

function useCrud(loadFn, createFn, updateFn, deleteFn) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { setItems(await loadFn()); } catch { setMsg({ type:'error', text:'Error cargando datos' }); }
  }, [loadFn]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = (defaults = {}) => { setForm(defaults); setAdding(true); setEditing(null); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); setAdding(false); };
  const cancel = () => { setAdding(false); setEditing(null); setForm({}); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await updateFn(editing.id, form);
      else await createFn(form);
      await load();
      cancel();
      setMsg({ type:'ok', text:'Guardado correctamente.' });
    } catch { setMsg({ type:'error', text:'Error al guardar.' }); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try { await deleteFn(id); await load(); }
    catch { setMsg({ type:'error', text:'Error al eliminar.' }); }
  };

  return { items, adding, editing, form, saving, msg, setField, openAdd, openEdit, cancel, save, del };
}

// ─── FORMATOS ───────────────────────────────────────────────────

function FormatosSection() {
  const { items, adding, editing, form, saving, msg, setField, openAdd, openEdit, cancel, save, del } =
    useCrud(
      () => publicidadConfigService.getFormatos(),
      dto  => publicidadConfigService.crearFormato(dto),
      (id,dto) => publicidadConfigService.actualizarFormato(id,dto),
      id   => publicidadConfigService.eliminarFormato(id)
    );

  const fields = [
    { key:'nombre',        label:'Nombre',         required:true },
    { key:'canal',         label:'Canal',          type:'select', options: CANALES.map(c=>({value:c,label:c})), required:true },
    { key:'fraccionPagina',label:'Fracción página', placeholder:'1/2, 1/4...' },
    { key:'columnasAncho', label:'Columnas ancho',  type:'number' },
    { key:'altoCm',        label:'Alto (cm)',       type:'number' },
    { key:'modulos',       label:'Módulos',         type:'number' },
    { key:'anchoPx',       label:'Ancho (px)',      type:'number' },
    { key:'altoPx',        label:'Alto (px)',       type:'number' },
    { key:'tiposArchivo',  label:'Tipos archivo',   placeholder:'PDF,JPG,MP4' },
    { key:'resolucionMinDpi', label:'Resolución mín DPI', type:'number' },
    { key:'perfilColor',   label:'Perfil color',    placeholder:'CMYK, RGB' },
    { key:'orden',         label:'Orden',           type:'number' },
    { key:'descripcion',   label:'Descripción',     type:'textarea', full:true },
  ];

  return (
    <CrudSection
      title='Formatos Publicitarios'
      items={items}
      columns={[
        { key:'nombre',  label:'Nombre' },
        { key:'canal',   label:'Canal' },
        { key:'fraccionPagina', label:'Fracción' },
        { key:'anchoPx', label:'Ancho px' },
        { key:'altoPx',  label:'Alto px' },
        { key:'tiposArchivo', label:'Tipos archivo' },
        { key:'activo',  label:'Estado', render: i => <span className={`${styles.badge} ${i.activo ? styles.badgeActivo : styles.badgeInactivo}`}>{i.activo ? 'Activo':'Inactivo'}</span> },
      ]}
      adding={adding} editing={editing}
      onAdd={() => openAdd({ orden: 0, canal:'digital' })}
      onEdit={openEdit} onDelete={del}
      renderForm={() => (
        <>
          {msg && <div className={`${styles.msg} ${msg.type==='ok'?styles.msgOk:styles.msgError}`}>{msg.text}</div>}
          <FormInline fields={fields} values={form} onChange={setField} onSave={save} onCancel={cancel} saving={saving} />
        </>
      )}
    />
  );
}

// ─── UBICACIONES ─────────────────────────────────────────────────

function UbicacionesSection() {
  const { items, adding, editing, form, saving, msg, setField, openAdd, openEdit, cancel, save, del } =
    useCrud(
      () => publicidadConfigService.getUbicaciones(),
      dto  => publicidadConfigService.crearUbicacion(dto),
      (id,dto) => publicidadConfigService.actualizarUbicacion(id,dto),
      id   => publicidadConfigService.eliminarUbicacion(id)
    );

  const fields = [
    { key:'nombre',       label:'Nombre',            required:true },
    { key:'canal',        label:'Canal',             type:'select', options: CANALES.map(c=>({value:c,label:c})), required:true },
    { key:'slugPosicion', label:'Slug posición',     placeholder:'home_top, sidebar, entre_articulos, revista_pagina' },
    { key:'recargoPct',   label:'Recargo %',         type:'number' },
    { key:'orden',        label:'Orden',             type:'number' },
    { key:'descripcion',  label:'Descripción',       type:'textarea', full:true },
  ];

  return (
    <CrudSection
      title='Ubicaciones y Posiciones'
      items={items}
      columns={[
        { key:'nombre',       label:'Nombre' },
        { key:'canal',        label:'Canal' },
        { key:'slugPosicion', label:'Slug' },
        { key:'recargoPct',   label:'Recargo %', render: i => `+${i.recargoPct}%` },
        { key:'activo',       label:'Estado', render: i => <span className={`${styles.badge} ${i.activo ? styles.badgeActivo : styles.badgeInactivo}`}>{i.activo ? 'Activo':'Inactivo'}</span> },
      ]}
      adding={adding} editing={editing}
      onAdd={() => openAdd({ recargoPct: 0, orden: 0, canal:'digital' })}
      onEdit={openEdit} onDelete={del}
      renderForm={() => (
        <FormInline fields={fields} values={form} onChange={setField} onSave={save} onCancel={cancel} saving={saving} />
      )}
    />
  );
}

// ─── RECARGOS ────────────────────────────────────────────────────

function RecargosSection() {
  const crom = useCrud(
    () => publicidadConfigService.getCromatismo(),
    dto  => publicidadConfigService.crearCromatismo(dto),
    (id,dto) => publicidadConfigService.actualizarCromatismo(id,dto),
    id   => publicidadConfigService.eliminarCromatismo(id)
  );
  const ed = useCrud(
    () => publicidadConfigService.getRecargosEdicion(),
    dto  => publicidadConfigService.crearRecargoEdicion(dto),
    (id,dto) => publicidadConfigService.actualizarRecargoEdicion(id,dto),
    id   => publicidadConfigService.eliminarRecargoEdicion(id)
  );

  const fieldsCrom = [
    { key:'nombre',     label:'Nombre',     required:true, placeholder:'Full Color, B&N...' },
    { key:'recargoPct', label:'Recargo %',  type:'number' },
    { key:'esDefault',  label:'Por defecto',type:'checkbox', checkLabel:'Sí' },
  ];
  const fieldsEd = [
    { key:'nombre',      label:'Nombre',      required:true, placeholder:'Dominical, Especial...' },
    { key:'recargoPct',  label:'Recargo %',   type:'number' },
    { key:'descripcion', label:'Descripción', type:'textarea', full:true },
  ];

  return (
    <>
      <CrudSection
        title='Recargos por Cromatismo'
        items={crom.items}
        columns={[
          { key:'nombre',     label:'Nombre' },
          { key:'recargoPct', label:'Recargo %', render: i => `+${i.recargoPct}%` },
          { key:'esDefault',  label:'Default',   render: i => i.esDefault ? '✓' : '' },
        ]}
        adding={crom.adding} editing={crom.editing}
        onAdd={() => crom.openAdd({ recargoPct: 0 })}
        onEdit={crom.openEdit} onDelete={crom.del}
        renderForm={() => (
          <FormInline fields={fieldsCrom} values={crom.form} onChange={crom.setField} onSave={crom.save} onCancel={crom.cancel} saving={crom.saving} />
        )}
      />
      <CrudSection
        title='Recargos por Tipo de Edición'
        items={ed.items}
        columns={[
          { key:'nombre',     label:'Nombre' },
          { key:'recargoPct', label:'Recargo %', render: i => `+${i.recargoPct}%` },
          { key:'descripcion',label:'Descripción' },
        ]}
        adding={ed.adding} editing={ed.editing}
        onAdd={() => ed.openAdd({ recargoPct: 0 })}
        onEdit={ed.openEdit} onDelete={ed.del}
        renderForm={() => (
          <FormInline fields={fieldsEd} values={ed.form} onChange={ed.setField} onSave={ed.save} onCancel={ed.cancel} saving={ed.saving} />
        )}
      />
    </>
  );
}

// ─── TIPOS DE CONTENIDO ──────────────────────────────────────────

function TiposContenidoSection() {
  const { items, adding, editing, form, saving, setField, openAdd, openEdit, cancel, save, del } =
    useCrud(
      () => publicidadConfigService.getTiposContenido(),
      dto  => publicidadConfigService.crearTipoContenido(dto),
      (id,dto) => publicidadConfigService.actualizarTipoContenido(id,dto),
      id   => publicidadConfigService.eliminarTipoContenido(id)
    );

  const fields = [
    { key:'nombre',              label:'Nombre',               required:true },
    { key:'tarifaFijaCop',       label:'Tarifa fija COP',      type:'number', placeholder:'Dejar vacío para cálculo variable' },
    { key:'requiereAprobacion',  label:'Requiere aprobación',  type:'checkbox', checkLabel:'Sí' },
    { key:'ivaIncluido',         label:'IVA incluido',         type:'checkbox', checkLabel:'Sí' },
    { key:'notasLegales',        label:'Notas legales',        type:'textarea', full:true },
  ];

  return (
    <CrudSection
      title='Tipos de Contenido'
      items={items}
      columns={[
        { key:'nombre',             label:'Nombre' },
        { key:'tarifaFijaCop',      label:'Tarifa fija', render: i => i.tarifaFijaCop ? `$${i.tarifaFijaCop.toLocaleString('es-CO')}` : '(variable)' },
        { key:'requiereAprobacion', label:'Aprobación',  render: i => i.requiereAprobacion ? 'Sí' : 'No' },
        { key:'ivaIncluido',        label:'IVA incl.',   render: i => i.ivaIncluido ? 'Sí' : 'No' },
      ]}
      adding={adding} editing={editing}
      onAdd={() => openAdd({ requiereAprobacion: true, ivaIncluido: false })}
      onEdit={openEdit} onDelete={del}
      renderForm={() => (
        <FormInline fields={fields} values={form} onChange={setField} onSave={save} onCancel={cancel} saving={saving} />
      )}
    />
  );
}

// ─── PARÁMETROS FISCALES ─────────────────────────────────────────

function FiscalSection() {
  const [fiscal, setFiscal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setFormState] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    publicidadConfigService.getFiscal()
      .then(setFiscal).catch(() => setFiscal(null));
  }, []);

  const setField = (k, v) => setFormState(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = fiscal
        ? await publicidadConfigService.actualizarFiscal(fiscal.id, form)
        : await publicidadConfigService.crearFiscal(form);
      setFiscal(r); setEditing(false);
      setMsg({ type:'ok', text:'Parámetros fiscales guardados.' });
    } catch { setMsg({ type:'error', text:'Error al guardar.' }); }
    setSaving(false);
  };

  const fields = [
    { key:'ivaPct',               label:'IVA %',                type:'number', required:true },
    { key:'descuentoAgenciaPct',  label:'Descuento agencia %',  type:'number', required:true },
    { key:'vigenteDesde',         label:'Vigente desde',        type:'date',   required:true },
    { key:'vigenteHasta',         label:'Vigente hasta',        type:'date' },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        Parámetros Fiscales (IVA y Agencia)
        <button className={styles.btnPrimary}
          style={{ marginLeft:'auto', fontSize:12, padding:'5px 12px' }}
          onClick={() => { setFormState(fiscal || { ivaPct:19, descuentoAgenciaPct:15 }); setEditing(true); }}
        >
          <Pencil size={13} /> {fiscal ? 'Editar' : 'Configurar'}
        </button>
      </div>
      {msg && <div className={`${styles.msg} ${msg.type==='ok'?styles.msgOk:styles.msgError}`}>{msg.text}</div>}
      {editing && (
        <FormInline fields={fields} values={form} onChange={setField} onSave={save} onCancel={() => setEditing(false)} saving={saving} />
      )}
      {fiscal && !editing && (
        <table className={styles.table}>
          <tbody>
            <tr><td><strong>IVA</strong></td><td>{fiscal.ivaPct}%</td></tr>
            <tr><td><strong>Descuento agencia</strong></td><td>{fiscal.descuentoAgenciaPct}%</td></tr>
            <tr><td><strong>Vigente desde</strong></td><td>{fiscal.vigenteDesde}</td></tr>
            <tr><td><strong>Vigente hasta</strong></td><td>{fiscal.vigenteHasta || 'Indefinido'}</td></tr>
          </tbody>
        </table>
      )}
      {!fiscal && !editing && <div className={styles.empty}>No hay parámetros fiscales configurados.</div>}
    </div>
  );
}

// ─── TARIFAS BASE ────────────────────────────────────────────────

function TarifasSection() {
  const [sistemas, setSistemas] = useState([]);
  useEffect(() => { publicidadConfigService.getSistemas().then(setSistemas).catch(() => {}); }, []);

  const { items, adding, editing, form, saving, setField, openAdd, openEdit, cancel, save, del } =
    useCrud(
      () => publicidadConfigService.getTarifas(),
      dto  => publicidadConfigService.crearTarifa(dto),
      (id,dto) => publicidadConfigService.actualizarTarifa(id,dto),
      id   => publicidadConfigService.eliminarTarifa(id)
    );

  const fields = [
    { key:'sistemaMedicionId', label:'Sistema medición', type:'select', required:true,
      options: sistemas.map(s => ({ value: s.id, label: s.nombre })) },
    { key:'nombre',        label:'Nombre',         required:true, placeholder:'Tarifa general 2026' },
    { key:'valorCop',      label:'Valor COP x unidad', type:'number', required:true },
    { key:'moneda',        label:'Moneda',         placeholder:'COP' },
    { key:'vigenteDesde',  label:'Vigente desde',  type:'date', required:true },
    { key:'vigenteHasta',  label:'Vigente hasta',  type:'date' },
    { key:'notas',         label:'Notas',          type:'textarea', full:true },
  ];

  return (
    <CrudSection
      title='Tarifas Base'
      items={items}
      columns={[
        { key:'nombre',         label:'Nombre' },
        { key:'sistemaMedicionNombre', label:'Sistema' },
        { key:'valorCop',       label:'Valor x unidad', render: i => `$${(i.valorCop||0).toLocaleString('es-CO')}` },
        { key:'vigenteDesde',   label:'Desde' },
        { key:'vigenteHasta',   label:'Hasta', render: i => i.vigenteHasta || '∞' },
        { key:'activo',         label:'Estado', render: i => <span className={`${styles.badge} ${i.activo ? styles.badgeActivo : styles.badgeInactivo}`}>{i.activo ? 'Activa':'Inactiva'}</span> },
      ]}
      adding={adding} editing={editing}
      onAdd={() => openAdd({ moneda:'COP', valorCop:0 })}
      onEdit={openEdit} onDelete={del}
      renderForm={() => (
        <FormInline fields={fields} values={form} onChange={setField} onSave={save} onCancel={cancel} saving={saving} />
      )}
    />
  );
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────

export default function TarifarioTab() {
  return (
    <div>
      <Collapsible title='Tarifas Base' defaultOpen>
        <TarifasSection />
      </Collapsible>
      <Collapsible title='Formatos Publicitarios'>
        <FormatosSection />
      </Collapsible>
      <Collapsible title='Ubicaciones y Posiciones'>
        <UbicacionesSection />
      </Collapsible>
      <Collapsible title='Recargos'>
        <RecargosSection />
      </Collapsible>
      <Collapsible title='Tipos de Contenido'>
        <TiposContenidoSection />
      </Collapsible>
      <Collapsible title='Parámetros Fiscales'>
        <FiscalSection />
      </Collapsible>
    </div>
  );
}
